import React, { useState, useEffect, useMemo, useCallback } from "react";
import Loading from "../../../utils/Loading";
import SearchFilter from "../../../utils/SearchFilter";
import {
  Button,
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useDisclosure,
  Input,
} from "@heroui/react";
import { Eye, EyeOff } from "../../../components/admin/DashboardIcons.jsx";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";

const BetTable = () => {
  const [bets, setBets] = useState([]);
  const [selectedBetIds, setSelectedBetIds] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [expanded, setExpanded] = useState({});
  const [betDetailsCache, setBetDetailsCache] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const {
    isOpen: isMessageOpen,
    onOpen: onMessageOpen,
    onOpenChange: onMessageOpenChange,
    onClose: onMessageClose,
  } = useDisclosure();
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("alert");
  const [modalCallback, setModalCallback] = useState(null);
  const [modalAutoCloseMs, setModalAutoCloseMs] = useState(0);
  const [isbetdetails, setIsBetDetails] = useState(false);
  const [selectedBet, setSelectedBet] = useState(null);
  const [betDetailsLoading, setBetDetailsLoading] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState(null);
  const [pendingBulkCancelIds, setPendingBulkCancelIds] = useState([]);
  const [userIdInput, setUserIdInput] = useState("");

  useEffect(() => {
    if (isMessageOpen && modalType === "alert" && modalAutoCloseMs > 0) {
      const timeoutId = setTimeout(() => {
        if (typeof modalCallback === "function") {
          const maybeFn = modalCallback();
          if (typeof maybeFn === "function") {
            maybeFn();
          }
        }
        if (typeof onMessageClose === "function") {
          onMessageClose();
        }
        setModalAutoCloseMs(0);
      }, modalAutoCloseMs);
      return () => clearTimeout(timeoutId);
    }
  }, [
    isMessageOpen,
    modalType,
    modalAutoCloseMs,
    modalCallback,
    onMessageClose,
  ]);

  const {
    isOpen: isPasswordOpen,
    onOpen: onPasswordOpen,
    onOpenChange: onPasswordOpenChange,
    onClose: onPasswordClose,
  } = useDisclosure();

  const {
    isOpen: isBetOpen,
    onOpen: onBetOpen,
    onOpenChange: onBetChange,
    onClose: onBetClose,
  } = useDisclosure();

  const fetchBetDetails = useCallback(async (bet_id) => {
    try {
      setDetailLoading((prev) => ({ ...prev, [bet_id]: true }));
      let matchResult = null;
      const response = await fetch(
        process.env.REACT_APP_API_HOST + "/getbetdetils",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify({
            betId: bet_id,
          }),
        },
      );
      const betdetail = await response.json();
      const matchId = betdetail.data[0].match_id;

      if (matchId) {
        const res = await fetch(
          `${process.env.REACT_APP_API_HOST}/getMatchResultById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("jwt"),
            },
            body: JSON.stringify({
              match_id: matchId,
            }),
          },
        );
        matchResult = await res.json();
      }
      const betDetails = {
        ...betdetail,
        results: matchResult,
      };
      setBetDetailsCache((prev) => ({ ...prev, [bet_id]: betDetails }));
      return betDetails;
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setDetailLoading((prev) => ({ ...prev, [bet_id]: false }));
    }
  }, []);

  const getbetdetails = async (bet_id) => {
    try {
      setBetDetailsLoading(true);
      setSelectedBet(null);
      onBetOpen();
      const details =
        betDetailsCache[bet_id] || (await fetchBetDetails(bet_id));
      setSelectedBet(details);
    } catch (err) {
      console.error("Unable to load bet details", err);
    } finally {
      setBetDetailsLoading(false);
    }
  };

  const calculateJodi = (open, close) => {
    const sumDigits = (numStr) => {
      if (!numStr || numStr.trim() === "") return null; // return null if empty
      const digits = numStr
        .split("")
        .map((d) => parseInt(d, 10))
        .filter((n) => !isNaN(n));
      if (digits.length === 0) return null;
      return digits.reduce((a, b) => a + b, 0);
    };

    const openSum = sumDigits(open);
    const closeSum = sumDigits(close);

    const openDigit = openSum !== null ? (openSum % 10).toString() : "-";
    const closeDigit = closeSum !== null ? (closeSum % 10).toString() : "_";

    // If both missing or invalid
    if (openSum === null && closeSum === null) return "--";

    // Always return 2-character Jodi
    return `${openDigit}${closeDigit}`;
  };

  // useEffect(() => {
  let timer;
  const getBetsHistory = async (userId) => {
    setLoading(true);
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const urlencoded = {
      user_id: userId ?? "",
    };

    let requestOptions = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(urlencoded),
    };
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/getBetsHistory`,
        requestOptions,
      );
      const result = await response.json();
      if (Array.isArray(result.data)) {
        setBets(result.data);
        setUserOptions([...new Set(result.data.map((item) => item.user_id))]);
        setOperatorOptions([
          ...new Set(result.data.map((item) => item.operator_name)),
        ]);
      } else {
        setBets([]);
        setUserOptions([]);
        setOperatorOptions([]);
      }
    } catch (err) {
      console.error("Fetch failed", err);
      setBets([]);
    } finally {
      timer = setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleSubmitUserId = () => {
    getBetsHistory(userIdInput);
  };

  // getBetsByOperatorId();
  // Removed initial fetch - user must enter user_id and submit

  //   return () => clearTimeout(timer);
  // }, []);
  const handleCancel = async (id) => {
    setPendingCancelId(id);
    setModalMessage("Are you sure you want to cancel this bet?");
    setModalType("confirm");
    setModalCallback(() => () => {
      setIsPasswordModalOpen(true);
    });

    onMessageOpen();
    // setModalMessage("Are you sure you want to cancel this bet?");
    // setModalType("confirm");
    // setModalCallback(() => () => {
    //   fetch(`${process.env.REACT_APP_API_HOST}/cancel/bet/${id}`, {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //       Authorization: `Bearer ${localStorage.getItem("jwt")}`,
    //     },
    //     body: JSON.stringify({ status: 4 }),
    //   })
    //     .then(async (response) => {
    //       if (!response.ok) throw new Error("Failed to cancel the bet");
    //       return response.json();
    //     })
    //     .then(() => {
    //       setModalMessage("Bet cancelled successfully!");
    //       setModalType("alert");
    //       setModalCallback(() => () => {
    //         getBetsByOperatorId();
    //       });
    //       setModalAutoCloseMs(1000);
    //       onMessageOpen();
    //     })
    //     .catch((error) => {
    //       console.error("Cancel failed:", error);
    //       setModalMessage("Failed to cancel the bet.");
    //       setModalType("alert");
    //       setModalCallback(null);
    //       onMessageOpen();
    //     });
    // });
    // onMessageOpen();
  };

  const submitPasswordAndCancel = async (e) => {
    e.preventDefault();

    setIsPasswordModalOpen(false);

    const isBulk = pendingBulkCancelIds.length > 0;

    const url = isBulk
      ? `${process.env.REACT_APP_API_HOST}/cancel/bulk`
      : `${process.env.REACT_APP_API_HOST}/cancel/bet/${pendingCancelId}`;
    console.log(pendingBulkCancelIds);
    const body = isBulk
      ? {
          bet_ids: pendingBulkCancelIds,
          status: 4,
          password: passwordValue,
          user_id: localStorage.getItem("uid"),
        }
      : {
          status: 4,
          password: passwordValue,
          user_id: localStorage.getItem("uid"),
        };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) {
        // Show backend message
        setModalMessage(data?.message || "Failed to cancel the bet.");
        setModalType("alert");
        setModalCallback(null);
        onMessageOpen();
        return;
      }

      // Clear password
      setPasswordValue("");
      setPendingBulkCancelIds([]);

      // Show success popup
      setModalMessage(
        isBulk
          ? "Selected bets cancelled successfully!"
          : "Bet cancelled successfully!",
      );
      setModalType("alert");
      setModalCallback(() => () => {
        getBetsHistory(userIdInput);
        if (isBulk) {
          setSelectedBetIds([]);
          setRowSelection({});
        }
      });
      setModalAutoCloseMs(1000);
      onMessageOpen();
    } catch (error) {
      console.error("Cancel failed:", error);

      setModalMessage("Failed to cancel the bet.");
      setModalType("alert");
      setModalCallback(null);
      onMessageOpen();
    }
  };

  const handleBulkCancel = async (rows) => {
    const selectedBetIds = rows.map((row) => row.bet_id);
    if (selectedBetIds.length === 0) {
      setModalMessage("Please select at least one bet to cancel.");
      setModalType("alert");
      setModalCallback(null);
      setModalAutoCloseMs(1000);
      onMessageOpen();
      return;
    }

    setPendingBulkCancelIds(selectedBetIds);

    setModalMessage("Are you sure you want to cancel the selected bets?");
    setModalType("confirm");
    setModalCallback(() => () => {
      setIsPasswordModalOpen(true);
      // fetch(`${process.env.REACT_APP_API_HOST}/cancel/bulk`, {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      //   },
      //   body: JSON.stringify({
      //     bet_ids: selectedBetIds,
      //     status: 4,
      //   }),
      // })
      //   .then(async (response) => {
      //     if (!response.ok) throw new Error("Failed to cancel selected bets");
      //     return response.json();
      //   })
      //   .then(() => {
      //     setModalMessage("Selected bets cancelled successfully!");
      //     setModalType("alert");
      //     setModalCallback(() => () => {
      //       getBetsByOperatorId();
      //       setSelectedBetIds([]);
      //     });
      //     setModalAutoCloseMs(1000);
      //     onMessageOpen();
      //   })
      //   .catch((error) => {
      //     console.error("Bulk cancel failed:", error);
      //     setModalMessage("Failed to cancel selected bets.");
      //     setModalType("alert");
      //     setModalCallback(null);
      //     onMessageOpen();
      //   });
    });
    onMessageOpen();
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate(),
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "bet_id",
        header: "Bet Id",
        size: "content",
        enableClickToCopy: true,
        // Cell: ({ cell, row }) => {
        //   const betId = cell.getValue();
        //   return (
        //     <button
        //       type="button"
        //       onClick={() => row.toggleExpanded()}
        //       className="text-blue-600 hover:underline flex items-center gap-1"
        //     >
        //       <span>{row.getIsExpanded() ? "▾" : "▸"}</span>
        //       {betId}
        //     </button>
        //   );
        // },
      },
      {
        accessorKey: "client_bet_id",
        header: "Client Bet Id",
        size: 300,
        enableClickToCopy: true,
      },
      {
        accessorKey: "round_id",
        header: "Round Id",
        size: 130,
        enableClickToCopy: true,
      },
      {
        accessorKey: "user_id",
        header: "User Id",
        size: 130,
        filterVariant: "select",
        mantineFilterSelectData: userOptions.map((value) => ({
          value: String(value),
          label: String(value),
        })),
        enableClickToCopy: true,
      },
      {
        accessorKey: "user_name",
        header: "User Name",
        size: 160,
        enableClickToCopy: true,
      },
      {
        accessorKey: "match_date",
        header: "Match Date",
        size: 160,
      },
      {
        accessorKey: "match_name",
        header: "Match Name",
        size: 160,
        filterVariant: "multi-select",
        enableColumnActions: true,
        enableColumnFilter: true,
        enableClickToCopy: true,
      },
      {
        accessorKey: "match_map_id",
        header: "Match Type",
        size: 140,
      },
      {
        accessorKey: "is_closed_type",
        header: "Market Type",
        size: 140,
      },
      {
        accessorKey: "operator_name",
        header: "Operator",
        size: 160,
        filterVariant: "select",
        mantineFilterSelectData: operatorOptions.map((value) => ({
          value,
          label: value,
        })),
        enableClickToCopy: true,
      },
      {
        accessorKey: "digit",
        header: "Digit",
        size: 110,
      },
      {
        accessorKey: "stake",
        header: "Stake",
        size: 110,
      },
      {
        accessorKey: "rate",
        header: "Rate",
        size: 110,
      },
      {
        accessorKey: "status_id",
        header: "Status Id",
        size: 130,
      },
      {
        accessorKey: "created_on",
        header: "Bet Time Date",
        size: 200,
        Cell: ({ cell }) => formatDateTime(cell.getValue()),
      },
      {
        accessorKey: "ip",
        header: "IP Address",
        size: 160,
        enableClickToCopy: true,
      },
      // {
      //   id: "action",
      //   header: "Action",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   size: 150,
      //   Cell: ({ row }) => {
      //     const isOpen = row.original.status_id === "Open";
      //     return (
      //       <div className="flex gap-2 items-start">
      //         <button
      //           className={`px-3 py-1 rounded text-sm text-white ${
      //             isOpen
      //               ? "bg-yellow-500 hover:bg-blue-600"
      //               : "bg-gray-400 cursor-not-allowed"
      //           }`}
      //           disabled={!isOpen}
      //           onClick={() => handleCancel(row.original.bet_id)}
      //         >
      //           Void
      //         </button>
      //         {/* <button
      //           className="px-3 py-0.5 rounded text-sm text-white bg-blue-600"
      //           onClick={() => getbetdetails(row.original.bet_id)}
      //         >
      //           <EyeOff className="w-4 h-4" />
      //         </button> */}
      //       </div>
      //     );
      //   },
      // },
    ],
    [getbetdetails, handleCancel, operatorOptions, userOptions],
  );

  const table = useMantineReactTable({
    columns,
    data: bets,
    enableRowSelection: true,
    enableColumnFilters: true,
    enableColumnFilterModes: true,
    enableGlobalFilter: true,
    enableSorting: true,
    enableExpanding: true,
    enableFacetedValues: true,
    enableColumnActions: false,
    enableHiding: false,
    enableDensityToggle: false,

    getRowId: (row) => String(row.bet_id ?? row.client_bet_id ?? ""),

    initialState: {
      pagination: { pageSize: 12 },
      showGlobalFilter: true,
      density: "sm",
    },

    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setQuickFilterText,

    state: {
      globalFilter: quickFilterText,
      rowSelection,
      expanded,
    },

    renderDetailPanel: ({ row }) => {
      const betId = row.original.bet_id;
      return (
        <BetDetailPanel
          betId={betId}
          detail={betDetailsCache[betId]}
          loading={Boolean(detailLoading[betId])}
          fetchDetails={fetchBetDetails}
          calculateJodi={calculateJodi}
        />
      );
    },

    // 👇 MUST be inside the config object AND wrapped correctly
    mantineSearchTextInputProps: {
      placeholder: `Search ${bets.length} rows`,
      sx: { minWidth: "300px" },
      variant: "filled",
    },
    mantineTableHeadCellProps: {
      className: "bg-admin_black text-white font-semibold",
      sx: {
        color: "#fff !important",
        fontWeight: 700,
        "& button[data-mantine-component='ActionIcon'] svg": {
          color: "#fff",
          fill: "#fff",
        },
      },
    },
    // mantineTopToolbarProps: {
    //   className: "bg-admin_black text-white",
    // },
  });

  const selectedBetRows = useMemo(
    () => table.getSelectedRowModel().flatRows.map((row) => row.original),
    [rowSelection, table],
  );

  const handleQuickFilterChange = (e) => {
    const value = e.target.value;
    setQuickFilterText(value);
    table.setGlobalFilter(value);
  };

  const expandAll = () => {
    const allKeys = table.getRowModel().rows.map((row) => row.id);
    const newExpanded = {};
    allKeys.forEach((key) => {
      newExpanded[key] = true;
    });
    setExpanded(newExpanded);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="shadow rounded-lg">
      {/* User ID search */}
      <div className="flex justify-between items-center  text-white px-4 py-3 rounded-t-lg">
        <div className="flex items-end flex-nowrap gap-2">
          <Input
            type="text"
            placeholder="Enter User ID"
            value={userIdInput}
            onValueChange={setUserIdInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmitUserId();
            }}
            classNames={{
              base: "max-w-xs",
              inputWrapper: "border-1 border-black min-h-10 h-10",
            }}
          />
          <Button
            className="bg-black text-white h-10 min-h-10"
            onPress={handleSubmitUserId}
            isDisabled={!userIdInput.trim()}
          >
            Get Bets
          </Button>
        </div>
        <div className="flex items-center">
          {selectedBetRows.length >= 2 && (
            <div>
              <button
                className="bg-yellow text-admin_black font-semibold px-4 py-2 rounded border border-admin_black"
                onClick={() => handleBulkCancel(selectedBetRows)}
              >
                Cancel Selected ({selectedBetRows.length})
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className="ag-theme-alpine bet-table-container h-full"
        style={{ overflow: "auto" }}
      >
        <MantineReactTable table={table} enableGlobalFilterModes />
      </div>
      <Modal
        isOpen={isMessageOpen}
        onOpenChange={onMessageOpenChange}
        classNames={{
          backdrop: "bg-black/35",
          wrapper: "flex items-center justify-center min-h-screen",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              {modalType === "confirm" && (
                <>
                  <ModalBody className="flex items-start justify-center min-h-[60px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                  <ModalFooter className="-mt-4">
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      onPress={() => {
                        if (typeof modalCallback === "function") {
                          const maybeFn = modalCallback();
                          if (typeof maybeFn === "function") {
                            maybeFn();
                          }
                        }
                        onClose();
                      }}
                    >
                      Yes
                    </Button>
                  </ModalFooter>
                </>
              )}

              {modalType === "alert" && (
                <>
                  <ModalBody className="flex items-center justify-center min-h-[80px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal isOpen={isBetOpen} onOpenChange={onBetChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Bet Details</ModalHeader>
              <ModalBody>
                {betDetailsLoading || !selectedBet ? (
                  <Loading />
                ) : (
                  <>
                    {/* Extract common values */}
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
                      <div className="grid grid-cols-3 gap-x-8 gap-y-4 text-sm">
                        {/* Row 1 */}
                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Bet ID
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.data?.[0]?.bet_id || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Round ID
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.data?.[0]?.round_id || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            User ID
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.data?.[0]?.user_id || "--"}
                          </p>
                        </div>

                        {/* Row 2 */}
                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Market
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.results?.data?.market_title || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Match
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.results?.data?.match_title || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Result Date
                          </p>
                          {selectedBet.results?.data?.open_result ||
                          selectedBet.results?.data?.close_result ? (
                            selectedBet.results?.data?.modified_on ? (
                              <p className="text-gray-800  mt-0.5 text-xs">
                                {new Date(
                                  selectedBet.results.data.modified_on,
                                ).toLocaleString()}
                              </p>
                            ) : (
                              <p className="italic text-gray-400 mt-0.5">--</p>
                            )
                          ) : (
                            <p className="italic text-gray-500 mt-0.5">
                              Not yet published
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Results */}
                    <div className="grid grid-cols-5 gap-2 text-sm mb-4">
                      {/* Open Result (2 cols wide) */}
                      <div className="col-span-2 p-2 bg-green-50 rounded-md text-green-700 text-center">
                        <span className="font-semibold">Open Result:</span>{" "}
                        {selectedBet.results?.data.open_result &&
                        Number(selectedBet.results?.data.open_result) >= 0
                          ? selectedBet.results.data.open_result
                          : "—"}
                      </div>

                      {/* Jodi Result (1 col wide, smaller) */}
                      <div className="col-span-1 p-2 bg-yellow-50 rounded-md text-yellow-700 text-center font-semibold">
                        Jodi:{" "}
                        {calculateJodi(
                          selectedBet.results?.data.open_result,
                          selectedBet.results?.data.close_result,
                        )}
                      </div>
                      {/* Close Result (2 cols wide) */}
                      <div className="col-span-2 p-2 bg-blue-50 rounded-md text-blue-700 text-center">
                        <span className="font-semibold">Close Result:</span>{" "}
                        {selectedBet.results?.data.close_result &&
                        Number(selectedBet.results?.data.close_result) >= 0
                          ? selectedBet.results.data.close_result
                          : "—"}
                      </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                        <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                          <tr>
                            <th className="px-3 py-2 text-left">Digit</th>
                            <th className="px-3 py-2 text-left">Bet Type</th>
                            <th className="px-3 py-2 text-right">Stake</th>
                            <th className="px-3 py-2 text-right">Profit</th>
                            <th className="px-3 py-2 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBet.data?.map((item) => (
                            <tr
                              key={item.bet_digits_id}
                              className="border-t border-gray-200"
                            >
                              <td className="px-3 py-2">{item.digit}</td>
                              <td className="px-3 py-2">
                                {item.is_closed_type?.data?.[0] == 0
                                  ? "OPEN"
                                  : "CLOSE"}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {item.stake}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {item.status_id === 2
                                  ? item.winning_potential_profit
                                  : "0"}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {item.status_id === 4 ? (
                                  <p className="text-sm italic text-red-300 text-center">
                                    Bet Cancelled
                                  </p>
                                ) : item.status_id === 5 ? (
                                  <p className="text-sm italic text-red-300 text-center">
                                    Bet Abandoned
                                  </p>
                                ) : item.status === "open" ? (
                                  !item.open_result ||
                                  item.open_result === "" ? (
                                    <p className="text-sm italic text-center">
                                      Open
                                    </p>
                                  ) : item.is_win === 1 ? (
                                    <p className="text-sm italic text-green-600 text-center">
                                      Settled Won
                                    </p>
                                  ) : item.is_win === 0 ? (
                                    <p className="text-sm italic text-red-600 text-center">
                                      Settled Loss
                                    </p>
                                  ) : null
                                ) : item.status === "close" ? (
                                  !item.close_result ||
                                  item.close_result === "" ? (
                                    <p className="text-sm italic text-center">
                                      Open
                                    </p>
                                  ) : item.is_win === 1 ? (
                                    <p className="text-sm italic text-green-600 text-center">
                                      Settled Won
                                    </p>
                                  ) : item.is_win === 0 ? (
                                    <p className=" text-sm italic text-red-600 text-center">
                                      Settled Loss
                                    </p>
                                  ) : null
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="danger"
                  variant="light"
                  onPress={() => {
                    setSelectedBet(null);
                    setBetDetailsLoading(false);
                    onClose();
                  }}
                >
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal isOpen={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <ModalContent>
          {(onClose) => (
            <form onSubmit={submitPasswordAndCancel}>
              <ModalHeader>Enter Password to Cancel the Bet</ModalHeader>
              <ModalBody>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={passwordValue}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  required
                  endContent={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="focus:outline-none"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  }
                />
              </ModalBody>
              <ModalFooter>
                <Button onPress={onClose}>Cancel</Button>
                <Button color="primary" type="submit">
                  Submit
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

const BetDetailPanel = ({
  betId,
  detail,
  loading,
  fetchDetails,
  calculateJodi,
}) => {
  useEffect(() => {
    if (!detail && betId && fetchDetails) {
      fetchDetails(betId);
    }
    // fetchDetails is stable (useCallback in parent), so this runs only
    // when betId or detail actually change.
  }, [betId, detail, fetchDetails]);

  if (loading && !detail) {
    return (
      <div className="p-4 text-sm text-gray-500 font-medium">Loading...</div>
    );
  }

  if (!detail) {
    return (
      <div className="p-4 text-sm text-gray-500">No details available.</div>
    );
  }

  const betSummary = detail.data?.[0] || {};
  const results = detail.results?.data;
  return (
    <div className="p-4 bg-gray-50 rounded border border-gray-200 text-sm w-full max-w-[900px]">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-gray-500 uppercase text-[10px] font-semibold">
            BET ID
          </p>
          <p className="text-gray-800 font-medium">
            {betSummary.bet_id ?? "--"}
          </p>
        </div>
        <div>
          <p className="text-gray-500 uppercase text-[10px] font-semibold">
            Round Id
          </p>
          <p className="text-gray-800 font-medium">
            {betSummary.round_id ?? "--"}
          </p>
        </div>
        <div>
          <p className="text-gray-500 uppercase text-[10px] font-semibold">
            User Id
          </p>
          <p className="text-gray-800 font-medium">
            {betSummary.user_id ?? "--"}
          </p>
        </div>
        <div>
          <p className="text-gray-500 uppercase text-[10px] font-semibold">
            Market
          </p>
          <p className="text-gray-800 font-medium">
            {results.market_title ?? "--"}
          </p>
        </div>
        <div>
          <p className="text-gray-500 uppercase text-[10px] font-semibold">
            Match
          </p>
          <p className="text-gray-800 font-medium">
            {results.match_title ?? "--"}
          </p>
        </div>
        <div>
          <p className="text-gray-500 uppercase text-[10px] font-semibold">
            Result Publish
          </p>
          <div className="text-gray-800 font-medium">
            {results?.open_result || results?.close_result ? (
              results?.modified_on ? (
                <p className="text-gray-800  mt-0.5 text-xs">
                  {new Date(results.modified_on).toLocaleString()}
                </p>
              ) : (
                <p className="italic text-gray-400 mt-0.5">--</p>
              )
            ) : (
              <p className="italic text-gray-500 mt-0.5">Not yet published</p>
            )}
          </div>
        </div>
      </div>

      {results && (
        <div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="p-2 bg-green-50 text-green-700 rounded">
              <span className="font-semibold block text-[10px] uppercase">
                Open Result
              </span>
              {results.open_result ?? "—"}
            </div>
            <div className="p-2 bg-yellow-50 text-yellow-700 rounded">
              <span className="font-semibold block text-[10px] uppercase">
                Jodi
              </span>
              {calculateJodi
                ? calculateJodi(results.open_result, results.close_result)
                : "—"}
            </div>
            <div className="p-2 bg-blue-50 text-blue-700 rounded">
              <span className="font-semibold block text-[10px] uppercase">
                Close Result
              </span>
              {results.close_result ?? "—"}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">Digit</th>
                  <th className="px-3 py-2 text-left">Bet Type</th>
                  <th className="px-3 py-2 text-right">Stake</th>
                  <th className="px-3 py-2 text-right">Profit</th>
                  <th className="px-3 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {detail.data?.map((item) => (
                  <tr
                    key={item.bet_digits_id}
                    className="border-t border-gray-200"
                  >
                    <td className="px-3 py-2">{item.digit}</td>
                    <td className="px-3 py-2">
                      {item.is_closed_type?.data?.[0] == 0 ? "OPEN" : "CLOSE"}
                    </td>
                    <td className="px-3 py-2 text-right">{item.stake}</td>
                    <td className="px-3 py-2 text-right">
                      {item.is_win === 1 ? item.winning_potential_profit : 0}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {item.status_id === 4 ? (
                        <p className="text-sm italic text-red-300 text-center">
                          Bet Cancelled
                        </p>
                      ) : item.status_id === 5 ? (
                        <p className="text-sm italic text-red-300 text-center">
                          Bet Abandoned
                        </p>
                      ) : item.status === "open" ? (
                        !item.open_result || item.open_result === "" ? (
                          <p className="text-sm italic text-center">Open</p>
                        ) : item.is_win === 1 ? (
                          <p className="text-sm italic text-green-600 text-center">
                            Settled Won
                          </p>
                        ) : item.is_win === 0 ? (
                          <p className="text-sm italic text-red-600 text-center">
                            Settled Loss
                          </p>
                        ) : null
                      ) : item.status === "close" ? (
                        !item.close_result || item.close_result === "" ? (
                          <p className="text-sm italic text-center">Open</p>
                        ) : item.is_win === 1 ? (
                          <p className="text-sm italic text-green-600 text-center">
                            Settled Won
                          </p>
                        ) : item.is_win === 0 ? (
                          <p className=" text-sm italic text-red-600 text-center">
                            Settled Loss
                          </p>
                        ) : null
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetTable;
